import { createFileRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router'
import { FeedbackPageContent, FeedbackProvider } from '@unsw/features/feedback'
import { RecordingsSidebar, useRecordingsWithPagination } from '@unsw/features/recordings'
import { useSidebar } from '@unsw/hooks'
import { useIsMobile } from '@unsw/hooks'
import { getUserRole } from '@unsw/hooks'
import { cn } from '@unsw/ui/lib/utils'
import { useEffect } from 'react'

type FeedbackSearch = {
  recordingId?: number
}

export const Route = createFileRoute('/dashboard/feedback')({
  beforeLoad: () => {
    const { isTeacher, isAdmin } = getUserRole()
    if (isTeacher || isAdmin) {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: FeedbackPage,
  validateSearch: (search: Record<string, unknown>): FeedbackSearch => {
    return {
      recordingId: search.recordingId ? Number(search.recordingId) : undefined,
    }
  },
})

function FeedbackPage() {
  const navigate = useNavigate({ from: '/dashboard/feedback' })
  const { recordingId } = useSearch({ from: '/dashboard/feedback' })
  const isMobile = useIsMobile()
  const { sidebarCollapsed, toggleSidebar, closeSidebar } = useSidebar()

  const { recordings, isLoading, hasMore, isLoadingMore, loadMore } = useRecordingsWithPagination()

  // Auto-select first recording when none is selected
  const firstRecordingId = recordings[0]?.id
  useEffect(() => {
    if (firstRecordingId && !recordingId) {
      navigate({ search: { recordingId: firstRecordingId }, replace: true })
    }
  }, [firstRecordingId, navigate, recordingId])

  // Navigate to Summary page when selecting a recording
  const handleSelectRecording = (id: number) => {
    navigate({
      to: '/dashboard/summary',
      search: { recordingId: id },
    })
  }

  // Treat undefined as desktop (false) for SSR safety
  const isMobileView = isMobile === true

  return (
    <div
      className={cn(
        'h-[calc(100vh-64px)] flex relative',
        // Desktop: with gap
        'md:gap-6',
        // Mobile: no gap
        'gap-0',
      )}
    >
      {/* Mobile: Backdrop (only visible when expanded) */}
      {isMobileView && (
        <button
          type="button"
          className={cn(
            'fixed top-[64px] inset-x-0 bottom-0 bg-black/50 z-30 transition-opacity duration-300',
            !sidebarCollapsed ? 'opacity-100' : 'opacity-0 pointer-events-none',
          )}
          onClick={closeSidebar}
          aria-label="Close sidebar"
        />
      )}

      {/* Sidebar - CSS-first for initial render, JS for interactions */}
      <div
        className={cn(
          'flex-shrink-0 bg-white z-40 transition-[width] duration-300 ease-in-out',
          // CSS-first: mobile gets narrow, desktop gets wide (correct on initial render)
          'w-14 border-r border-black/10',
          'md:w-80 md:rounded-lg md:border md:shadow-sm md:border-r-0',
          // JS override: mobile expanded state (only when confirmed mobile + expanded)
          isMobileView && !sidebarCollapsed && '!w-72 shadow-xl',
        )}
      >
        <RecordingsSidebar
          recordings={recordings}
          selectedRecordingId={recordingId ?? null}
          onSelectRecording={handleSelectRecording}
          isLoading={isLoading}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          onLoadMore={loadMore}
          isCollapsed={isMobile !== false && sidebarCollapsed}
          onToggle={toggleSidebar}
        />
      </div>

      {/* Main Content - Feedback with Provider */}
      <main
        className={cn(
          'flex-1 bg-white overflow-hidden',
          // Desktop: with border and rounded corners
          'md:rounded-lg md:border md:border-black/10 md:shadow-sm',
        )}
      >
        <FeedbackProvider recordingId={recordingId ?? null}>
          <FeedbackPageContent recordingId={recordingId ?? null} />
        </FeedbackProvider>
      </main>
    </div>
  )
}
